
import './Main.css';
import React from "react";
import Number from "./Number";
import * as M from "../../Modal";
import PieChart from "./PieChart";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import { T } from "../../../Constants";
import * as S from "../../../services";
import BarChart, { BarChartRef } from "./BarChart";
import Bubble, { BubbleProps, BubbleRef } from "./Bubble";

type Panel = Parameters<T.API.Report.Dashboard.GetESGSectionData>[0]["only"];
type Flags = Parameters<T.API.Report.Dashboard.GetESGSectionData>[0]["other"];

export type SectionItem = {
    /** The title of the section */
    title: string;
    /** The code of the section */
    code: T.Superset.CategorySection;
    /** The color palette to use */
    palette: string[];
    /** The current session id */
    session_id: string;
}

export type SectionProps = SectionItem & {
    /** A function to create a visual reference of the filters applied */
    filters?: (code: SectionItem["code"]) => T.AllowArray<React.ReactElement>;
    /** A callback to check if the section is empty */
    isEmpty?: (isEmpty: boolean) => void;
}

export type SectionRef = {
    /** A callback to refresh the section */
    refresh: () => void;
}

const styles: Record<"title" | "container" | "left_column" | "right_column" | "left_upper_row" | "left_lower_row" | "left_upper_row_left" | "left_upper_row_right", React.CSSProperties> = {
    title: { margin: "5px" },
    right_column: { flex: 7, margin: '5px' },
    left_lower_row: { flex: 1, margin: "5px" },
    left_upper_row: { display: 'flex', flex: 1 },
    left_upper_row_left: { flex: 1, margin: '5px' },
    left_upper_row_right: { flex: 1.5, margin: '5px' },
    left_column: { flex: 5, display: 'flex', flexDirection: 'column' },
    container: { display: "flex", height: "800px", marginBottom: "50px" },
};

const Section = React.forwardRef<SectionRef, SectionProps>(({ filters, isEmpty, ...props }, ref) => {
    const lg = H.useLanguage();
    const bar_ref = React.useRef<BarChartRef>(null);
    const bubble_ref = React.useRef<BubbleRef>(null);
    const empty_section_ref = React.useRef<boolean>(false);
    const [loading_panel, set_loading_panel] = React.useState<Panel>(null);
    // bubble data is like the global data, but with the bubble data
    const [bubble_data, set_bubble_data] = React.useState<ReturnType<T.API.Report.Dashboard.GetESGSectionData>>(null);
    // The info of the bubble to display retrieve from the bubble graph itself when it is clicked
    const [bubble_info, set_bubble_info] = React.useState<T.PartialBy<Record<"id" | "energy" | "name" | "color", string>, "energy" | "color"> & Record<"data", BubbleProps["bubble_info"]>>(null);
    // The global data of the section
    const [global_data, set_data, data_status] = H.useAsyncState<ReturnType<T.API.Report.Dashboard.GetESGSectionData>>({ number: [], pie: [], bar: [], bubble: [], units: null, labels: null });
    // The keys of the data to check if the section is empty
    const sectionKeys = ['number', 'bar', 'pie', 'bubble'];


    // Put in a ref to avoid the graph being re-rendered before the data is loaded when the bubble info changes
    const bubble_info_ref = React.useRef(bubble_info);
    React.useImperativeHandle(bubble_info_ref, () => bubble_info);
    const visual_filters = React.useMemo(() => filters?.(props.code), [filters, props.code]);

    const data = React.useMemo(() => {
        // For either case, the data for the bubble graph is the same, so return the global one
        if (bubble_data) return { ...bubble_data, bubble: global_data.bubble };
        else return global_data;
    }, [bubble_data, global_data]);

    // Load the data for the section, it's the default behavior
    React.useEffect(() => {
        let isSubscribed = true;
        S.getESGSectionData({ energy_code: props.code, session_id: props.session_id, other: { is_segmented: true, is_spread: true } })
            .then(res => {
                if (isSubscribed) {
                    // Check if all sections are empty after loading data
                    const isSectionEmpty = sectionKeys.every(key => Array.isArray(res.data[key]) && res.data[key].length === 0);
                    isEmpty?.(isSectionEmpty);
                    if (isSectionEmpty) set_data({ number: [], pie: [], bar: [], bubble: [], units: null, labels: null }, "load");
                    else set_data(res.data, "done");
                }
            })
            .catch(() => isSubscribed && set_data({ number: [], pie: [], bar: [], bubble: [], units: null, labels: null }, "error"));
        return () => {
            isSubscribed = false;
            set_data({ number: [], pie: [], bar: [], bubble: [], units: null, labels: null }, "load");
        }
    }, [props.code, props.session_id, set_data, isEmpty]);

    // Loading the data when a bubble is selected
    React.useEffect(() => {
        if (bubble_info) {
            let isSubscribed = true;
            const dismount = M.renderLoader();

            const params: Parameters<typeof S.getESGSectionData>[0] = {
                energy_code: props.code,
                session_id: props.session_id,
                sub_item: { id: bubble_info.id, energy: bubble_info.energy },
                other: {
                    is_spread: bar_ref.current?.smoothed,
                    is_segmented: bubble_ref.current?.segmented,
                    use_last_year: bubble_ref.current?.last_year,
                },
            };

            S.getESGSectionData(params)
                .then(res => isSubscribed && set_bubble_data(res.data))
                .catch(() => isSubscribed && set_bubble_data(null))
                .finally(dismount);

            return () => {
                isSubscribed = false;
            }
        }
        else set_bubble_data(null);
    }, [bubble_info, props.code, props.session_id]);

    React.useImperativeHandle(ref, () => ({
        refresh: () => {
            set_bubble_info(null);
            set_bubble_data(null);
            const dismount = M.renderLoader();

            const params: Parameters<typeof S.getESGSectionData>[0] = {
                energy_code: props.code,
                session_id: props.session_id,
                other: {
                    is_spread: bar_ref.current?.smoothed,
                    is_segmented: bubble_ref.current?.segmented,
                    use_last_year: bubble_ref.current?.last_year,
                },
            };
            S.getESGSectionData(params)
                .then(res => set_data(res.data))
                .catch(() => set_data({ number: [], pie: [], bar: [], bubble: [], units: null, labels: null }))
                .finally(dismount);
        },
        isEmpty: () => empty_section_ref.current,
    }), [props.code, props.session_id, bubble_info]);

    const refresh_component = React.useCallback((component: Panel, params?: Flags) => {
        set_loading_panel(component);
        const setter = bubble_info_ref.current ? set_bubble_data : set_data;

        const api_params: Parameters<typeof S.getESGSectionData>[0] = {
            other: params,
            only: component,
            energy_code: props.code,
            session_id: props.session_id,
            sub_item: bubble_info_ref.current && component !== "bubble" ? { id: bubble_info_ref.current.id, energy: bubble_info_ref.current.energy } : undefined,
        };
        S.getESGSectionData(api_params)
            .then(res => {
                setter(p => ({ ...p, [component]: res.data[component] }))
                if (component === "bubble") set_bubble_info(null);
            })
            .catch(M.Alerts.loadError)
            .finally(() => set_loading_panel(null));
    }, [set_data, props.code, props.session_id]);

    const refresh = React.useMemo<Record<Panel, (params?: Flags) => void>>(() => ({
        pie: params => refresh_component("pie", params),
        bar: params => refresh_component("bar", params),
        bubble: params => refresh_component("bubble", params),
        number: params => refresh_component("number", params),
    }), [refresh_component]);

    const click_bubble = React.useCallback<BubbleProps["on_bubble_click"]>((id, name, energy_type, color, reset_selected_bubble, data) => {
        bubble_info_ref.current = bubble_info_ref.current?.id === id || reset_selected_bubble ? null : { id, energy: energy_type, name, color, data };
        set_bubble_info(p => (p?.id === id || reset_selected_bubble) ? null : { id, energy: energy_type, name, color, data });
    }, []);

    const title = React.useMemo(() => {
        if (bubble_info?.name) return lg.getStaticText(props.title) + " - " + bubble_info.name;
        else return props.title;
    }, [props.title, bubble_info, lg]);

    React.useEffect(() => {
        if (data.number.length === 0 && data.bar.length === 0 && data.pie.length === 0 && data.bubble.length === 0) {
            empty_section_ref.current = true;
        }
    }, [data]);

    if (sectionKeys.every(key => Array.isArray(data[key]) && data[key].length === 0)) return null;
    return <C.Spinner status={data_status} min_load_size={styles.container.height as string}>
        <C.Title className="flex-grow-1" level={3} text={title} style={styles.title} />
        {visual_filters}

        <div style={styles.container}>
            {/* Left Column */}
            <div style={styles.left_column}>

                <div style={styles.left_upper_row}>
                    <div style={styles.left_upper_row_left}>
                        <Number
                            data={data.number}
                            refresh={refresh.number}
                            unit={data.units?.number || ""}
                            label={data.labels?.number || ""}
                            refreshing={loading_panel === "number"}
                            energy={props.code}
                        />
                    </div>

                    <div style={styles.left_upper_row_right}>
                        <PieChart
                            data={data.pie}
                            refresh={refresh.pie}
                            palette={props.palette}
                            unit={data.units?.pie || ""}
                            refreshing={loading_panel === "pie"}
                            energy={props.code}
                            color={bubble_info?.color}
                        />
                    </div>
                </div>

                <div style={styles.left_lower_row}>
                    <BarChart
                        ref={bar_ref}
                        data={data.bar}
                        refresh={refresh.bar}
                        palette={props.palette}
                        unit={data.units?.bar || ""}
                        refreshing={loading_panel === "bar"}
                        energy={props.code}
                        color={bubble_info?.color}
                    />
                </div>

            </div>

            {/* Right Column */}
            <div style={styles.right_column}>
                <Bubble
                    ref={bubble_ref}
                    data={data.bubble}
                    palette={props.palette}
                    refresh={refresh.bubble}
                    on_bubble_click={click_bubble}
                    unit={data.units?.bubble || ""}
                    is_energy={props.code === "energy"}
                    is_co2={props.code === "co2"}
                    refreshing={loading_panel === "bubble"}
                    y_axis_title={data.labels?.bubble || ""}
                    energy={props.code}
                    bubble_info={bubble_info?.data}
                />
            </div>
        </div>
    </C.Spinner>;
});

Section.displayName = "ESG_Section";
export default Section;